import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isTerminal } from '@platform/schema/generation';

import { elapsedOf, formatDuration, stageOf } from './stage';

import type {
  GenerationDetail,
  PictureBatch,
} from '@platform/schema/generation';

/** Ids as plain strings: the SDK's detail carries them unbranded. */
interface Lesson {
  rowId: string;
  lessonId?: string;
  name: string;
  picturesPending?: number;
}

type Job = Pick<
  GenerationDetail,
  'label' | 'status' | 'lessons' | 'startedAt' | 'finishedAt' | 'progressAt'
> & {
  current?: Omit<
    NonNullable<GenerationDetail['current']>,
    'rowId' | 'lessonId'
  > & {
    lessonId?: string;
  };
  completedLessons: readonly Lesson[];
  failedLessons: readonly Lesson[];
  remainingLessons: readonly Lesson[];
  pictures?: readonly PictureBatch[];
};

/** What the job is doing now, in the words `stage.ts` uses for a row. */
function doing(job: Job): string {
  // Before `current`: cancel leaves a running row to finish, and the job is still canceled.
  if (job.status === 'CANCELLED') return 'canceled';
  if (job.current !== undefined) return stageOf(job.current) ?? 'working';
  if (job.status === 'QUEUED') return 'waiting to start';
  return isTerminal(job.status) ? 'finished' : 'starting';
}

/**
 * A time, and its age while the job runs. The caller's clock stops when the job settles,
 * so an age printed after that would freeze at the moment it stopped.
 */
function lastUpdate(job: Job, now: number): string {
  if (job.progressAt === undefined) return '—';
  const at = new Date(job.progressAt).toLocaleTimeString();
  return isTerminal(job.status)
    ? at
    : `${at} (${formatDuration(now - Date.parse(job.progressAt))} ago)`;
}

const named = (lesson: Pick<Lesson, 'lessonId' | 'name'>): string =>
  lesson.lessonId === undefined
    ? lesson.name
    : `${lesson.lessonId} · ${lesson.name}`;

function LessonList({
  title,
  lessons,
}: {
  title: string;
  lessons: readonly Lesson[];
}) {
  return (
    <Box sx={{ minWidth: 200 }}>
      <Typography variant="subtitle2" component="h3">
        {title} ({lessons.length})
      </Typography>
      {lessons.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {lessons.map((lesson) => (
            <Typography component="li" variant="body2" key={lesson.rowId}>
              {named(lesson)}
              {lesson.picturesPending === undefined
                ? ''
                : ` — ${String(lesson.picturesPending)} picture(s) pending`}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

const usd = (amount: number): string => `$${amount.toFixed(2)}`;

function Lines({
  lines,
  prefix,
  color,
}: {
  lines: readonly string[];
  prefix: string;
  color: string;
}) {
  if (lines.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {lines.map((line) => (
        <Typography component="li" variant="body2" color={color} key={line}>
          {prefix}: {line}
        </Typography>
      ))}
    </Box>
  );
}

/** One module's batch of pictures — GEN-243: where it is, what it cost, what is missing. */
function Batch({ batch }: { batch: PictureBatch }) {
  const drawn = batch.requested - batch.missing.length;
  return (
    <Box>
      <Typography variant="body2">
        Pictures for {batch.moduleSlug}:{' '}
        {batch.pending
          ? `${String(batch.requested)} sent as one batch, waiting for it to return`
          : `${String(drawn)} of ${String(batch.requested)} drawn. The batch returned ${String(batch.received)} image(s), ${usd(batch.batchUsd)} against ${usd(batch.standardUsd)} at standard rate; ${String(batch.redrawn)} redrawn at standard rate`}
      </Typography>
      {batch.overbilled && (
        <Typography variant="body2" color="error">
          The batch returned more images than it asked for. No more batches are
          sent until this is checked.
        </Typography>
      )}
      <Lines lines={batch.missing} prefix="Not drawn" color="error" />
      <Lines lines={batch.findings} prefix="Check" color="warning.main" />
    </Box>
  );
}

/**
 * Where one job stands — CURR-29, read off the fields CURR-27 stores: the lesson it is on,
 * what it is doing, how long it has run and how long since it last moved. The last is the
 * one that tells a slow job from a stuck one, which a spinner never could.
 */
export function JobNow({ job, now }: { job: Job; now: number }) {
  const { current } = job;
  const lesson =
    current?.lessonName === undefined
      ? undefined
      : named({ ...current, name: current.lessonName });

  return (
    <Stack spacing={1} role="status" aria-label={`${job.label} now`}>
      <Typography variant="body2">
        {job.label}: {job.completedLessons.length} of {job.lessons.total}{' '}
        lessons done
      </Typography>
      <Typography variant="body2">
        {lesson === undefined ? 'Now' : `Now on ${lesson}`}: {doing(job)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Elapsed {elapsedOf(job, now) ?? '—'} · last update{' '}
        {lastUpdate(job, now)}
      </Typography>
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
        <LessonList title="Completed" lessons={job.completedLessons} />
        <LessonList title="Failed" lessons={job.failedLessons} />
        <LessonList title="Remaining" lessons={job.remainingLessons} />
      </Stack>
      {job.pictures?.map((batch) => (
        <Batch key={batch.moduleSlug} batch={batch} />
      ))}
    </Stack>
  );
}
